import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';

const LanguageSelector = ({ variant = 'outlined', size = 'small' }) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' }
  ];

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    i18n.changeLanguage(newLanguage);
    
    // Store the language preference in localStorage
    localStorage.setItem('soldikeeper-language', newLanguage);
    
    // Persist to backend
    const token = localStorage.getItem('token');
    if (token) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';
      fetch(`${API_BASE_URL}/user-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ language: newLanguage })
      }).catch(() => {}); // fire and forget
    }
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        minWidth: isMobile ? 120 : 150
      }}
    >
      {!isMobile && (
        <LanguageIcon 
          sx={{ 
            color: theme.palette.text.secondary,
            fontSize: 20
          }} 
        />
      )}
      
      <FormControl 
        variant={variant} 
        size={size}
        sx={{ 
          minWidth: isMobile ? 100 : 120,
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: isMobile ? 0.5 : 1
          }
        }}
      >
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          displayEmpty
          renderValue={(selected) => {
            const lang = languages.find(l => l.code === selected);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '16px' }}>{lang?.flag}</span>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    display: isMobile ? 'none' : 'block'
                  }}
                >
                  {lang?.name}
                </Typography>
              </Box>
            );
          }}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            }
          }}
        >
          {languages.map((language) => (
            <MenuItem 
              key={language.code} 
              value={language.code}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                minHeight: 40
              }}
            >
              <span style={{ fontSize: '18px' }}>{language.flag}</span>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {language.name}
              </Typography>
              {i18n.language === language.code && (
                <Box
                  sx={{
                    ml: 'auto',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main
                  }}
                />
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default LanguageSelector;
